import { useEffect, useMemo, useState } from "react";
import { levelMeta } from "@/lib/levels";
import { Users, Copy, Eye, Bell, Link2, Clock, TrendingDown, Mail, HandHeart } from "@/lib/icons";
import { NudgeModal } from "@/components/clinician/NudgeModal";
import { inviteEmailText } from "@/lib/betaInvite";
import { toast } from "sonner";

export function InviteCodeButtons({ code, onCopyCode, onCopyLink }) {
  if (!code) return null;
  const copyEmail = () => {
    navigator.clipboard.writeText(inviteEmailText(code, window.location.origin));
    toast.success("Invite email copied — paste it into your mail app and send to your testers.");
  };
  return (
    <div className="flex items-stretch gap-2 flex-wrap">
      <button onClick={onCopyCode} data-testid="copy-clinician-code" className="warm-card px-4 py-3 flex items-center gap-3 text-left">
        <div>
          <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">Your invite code</div>
          <div className="font-mono text-xl font-bold text-amber-800">{code}</div>
        </div>
        <Copy className="w-4 h-4 text-stone-500" />
      </button>
      <button onClick={onCopyLink} data-testid="copy-invite-link" className="warm-card px-4 py-3 flex items-center gap-2 text-xs font-semibold text-stone-700 hover:text-amber-800">
        <Link2 className="w-4 h-4" /> Copy invite link
      </button>
      <button onClick={copyEmail} data-testid="copy-invite-email" className="warm-card px-4 py-3 flex items-center gap-2 text-xs font-semibold text-stone-700 hover:text-amber-800">
        <Mail className="w-4 h-4" /> Copy invite email
      </button>
    </div>
  );
}

export function AlertNotifications({ notifications, onSelect, onDismiss }) {
  if (!notifications?.length) return null;
  return (
    <div className="mb-6 rounded-3xl p-4 border border-amber-300 bg-amber-50 flex items-start gap-3 flex-wrap" data-testid="alert-notifications">
      <Bell className="w-4 h-4 text-amber-800 mt-0.5" />
      <div className="flex-1 min-w-[240px]">
        <div className="text-xs font-bold uppercase tracking-widest text-amber-800 mb-1">New wellbeing flags since your last visit</div>
        <ul className="space-y-0.5">
          {notifications.map((n) => (
            <li key={n.id} className="text-sm text-stone-800" data-testid={`notification-${n.id}`}>
              <button className="font-semibold underline-offset-2 hover:underline" onClick={() => onSelect(n.client_id)}>{n.client_name}</button>
              {" — "}{n.alert.label} {n.alert.key === "nudge_hard_week" ? (n.alert.detail ? `· "${n.alert.detail}"` : "") : n.alert.key === "new_client" ? (n.alert.detail || "") : n.alert.key === "panic" ? `on ${n.alert.days} logged days in a row` : `${n.alert.threshold} for ${n.alert.days} logged days`}
              <span className="text-stone-400 text-xs"> · {new Date(n.created_at).toLocaleDateString()}</span>
            </li>
          ))}
        </ul>
      </div>
      <button data-testid="notifications-dismiss" onClick={onDismiss} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-stone-900 text-white hover:bg-amber-700">Got it</button>
    </div>
  );
}

const byAttention = (a, b) => {
  const da = a.days_since_checkin ?? Infinity, dbb = b.days_since_checkin ?? Infinity;
  if (da !== dbb) return dbb - da;
  return (b.worsening?.length || 0) - (a.worsening?.length || 0) || a.name.localeCompare(b.name);
};

function QuietChip({ c, onNudge }) {
  const d = c.days_since_checkin;
  if (d != null && d < 3) return null;
  const recentlyNudged = c.last_nudged_at && Date.now() - new Date(c.last_nudged_at).getTime() < 3 * 864e5;
  return (
    <span className="inline-flex items-center gap-1.5">
      {d == null ? <span className="text-stone-500" data-testid={`client-quiet-${c.id}`}>no check-ins yet</span>
        : <span className="inline-flex items-center gap-1 text-amber-800 font-bold" data-testid={`client-quiet-${c.id}`}><Clock className="w-3 h-3" /> {d}d quiet</span>}
      <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); onNudge(c); }} onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onNudge(c); } }}
        data-testid={`nudge-client-${c.id}`} title={recentlyNudged ? "Nudged in the last 3 days" : "Send a gentle nudge"}
        className={`inline-flex items-center gap-0.5 font-sans font-semibold px-1.5 py-0.5 rounded-md ${recentlyNudged ? "text-stone-400 bg-stone-100" : "text-white bg-amber-700 hover:bg-amber-800"}`}>
        <HandHeart className="w-3 h-3" /> {recentlyNudged ? "nudged" : "nudge"}
      </span>
    </span>
  );
}

function TrendBadge({ c }) {
  if (!c.worsening?.length) return null;
  const title = c.worsening.map((w) => `${w.label} ${w.delta > 0 ? "+" : ""}${w.delta} vs prior 3 weeks`).join("\n");
  return (
    <span className="inline-flex items-center gap-0.5 text-rose-700 font-bold" title={title} data-testid={`client-trend-${c.id}`}>
      <TrendingDown className="w-3 h-3" /> trend · {c.worsening.map((w) => w.label.split(" ")[0].toLowerCase()).join(", ")}
    </span>
  );
}

export function ClientList({ clients, selected, onSelect, onNudged }) {
  const [nudging, setNudging] = useState(null);
  const [sort, setSort] = useState(() => sessionStorage.getItem("hc_csort") || "attention");
  useEffect(() => { sessionStorage.setItem("hc_csort", sort); }, [sort]);
  const ordered = useMemo(() => [...clients].sort(sort === "az" ? (a, b) => a.name.localeCompare(b.name) : byAttention), [clients, sort]);
  const btn = (k, label, testid) => (
    <button key={k} onClick={() => setSort(k)} data-testid={testid} aria-pressed={sort === k}
      className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-md ${sort === k ? "bg-stone-900 text-white" : "text-stone-500 hover:text-stone-800"}`}>{label}</button>
  );
  return (
    <div className="xl:col-span-3 space-y-3 min-w-0">
      <div className="flex items-center justify-between gap-2 text-stone-800">
        <div className="flex items-center gap-2"><Users className="w-4 h-4" /><span className="text-xs font-bold uppercase tracking-widest">People ({clients.length})</span></div>
        <div className="flex gap-0.5 rounded-lg bg-stone-100 p-0.5" data-testid="client-sort">{btn("attention", "Needs attention", "client-sort-attention")}{btn("az", "A–Z", "client-sort-az")}</div>
      </div>
      {clients.length === 0 && <div className="warm-card p-5 text-sm text-stone-500">No one here yet. Share your connect code — or ask a friend to grant you Coach view from their My people page.</div>}
      {ordered.map((c) => (
        <button key={c.id} data-testid={`client-item-${c.id}`} onClick={() => onSelect(c.id)}
          className={`w-full text-left warm-card p-4 transition ${selected === c.id ? "ring-2 ring-amber-600 border-amber-300" : ""}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="font-semibold text-stone-900">{c.name}</div>
            {c.days_since_checkin != null && c.days_since_checkin < 3 && <span className="text-[10px] font-mono text-emerald-700" data-testid={`client-recent-${c.id}`}>{c.days_since_checkin === 0 ? "today" : `${c.days_since_checkin}d ago`}</span>}
          </div>
          <div className="text-xs text-stone-500 mt-0.5">{c.email}</div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-xs font-mono text-stone-500">
            <span>{c.active_habits} habit{c.active_habits === 1 ? "" : "s"}</span>
            <span>{c.total_checkins} check-in{c.total_checkins === 1 ? "" : "s"}</span>
            <QuietChip c={c} onNudge={setNudging} />
            {c.last_nudge_reply && Date.now() - new Date(c.last_nudge_reply.at).getTime() < 7 * 864e5 && (
              <span title={`${c.last_nudge_reply.label}${c.last_nudge_reply.note ? ` — "${c.last_nudge_reply.note}"` : ""}`} data-testid={`client-reply-${c.id}`}
                className={`font-sans font-semibold px-1.5 py-0.5 rounded-md ${c.last_nudge_reply.reply === "hard_week" ? "bg-rose-100 text-rose-800" : "bg-emerald-100 text-emerald-800"}`}>↩ {c.last_nudge_reply.label}</span>
            )}
            <TrendBadge c={c} />
            {c.stage_headsup_count > 0 && <span className="font-sans text-stone-500" data-testid={`client-stage-headsup-${c.id}`}>{c.stage_headsup_count} stage{c.stage_headsup_count === 1 ? "" : "s"} to look at</span>}
            {c.alert_count > 0 && (
              <span className="inline-flex items-center gap-1 text-amber-800 font-bold" data-testid={`client-alert-badge-${c.id}`}>
                <Eye className="w-3 h-3" /> {c.alert_count} flag{c.alert_count === 1 ? "" : "s"}
              </span>
            )}
          </div>
          {c.level && (
            <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-amber-800" data-testid={`client-level-${c.id}`}>
              {(() => { const { Icon } = levelMeta(c.level.name); return <Icon className="w-3.5 h-3.5" />; })()} Lv {c.level.index} {c.level.name} · {c.level.points} pts
            </div>
          )}
        </button>
      ))}
      {nudging && <NudgeModal client={nudging} onClose={() => setNudging(null)} onSent={onNudged} />}
    </div>
  );
}
